import { FC, ReactElement, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link } from '@digital-pulse/react';
import Form from '../../components/form/form';
import PageAlerts from '../../components/alert/pageAlerts';
import useIsHydrated from '../../lib/utils/useIsHydrated';
import useGuestPhoneDOBLookup from '../../hooks/useGuestPhoneDOBLookupMutation';
import { PAGE_TYPE, PERSONAL_INFO_VALIDATION } from '../../lib/constants/personalInfo';
import { LOOKUP_PAGE_TITLE } from '../../lib/constants';
import {
  CAREMARK_LOB,
  convertPhoneFormatForAPI,
  findLob,
  getLobData,
  isCaremark,
  isSpecialty,
  parseDobString,
  SPECIALTY_LOB
} from '../../lib/utils/utils';
import AppLayout from '../../components/appLayout/appLayout';
import formStyles from '../../components/form/form.module.scss';
import styles from './phone-dob.module.scss';
import { clickAnalytics, pageLoadAnalytics, tags } from '../../lib/utils/analytics';
import useCheckUsersAccount from '../../hooks/useCheckUsersAccount';
import { useRouter } from 'next/router';
import Spinner from '@/components/spinner';
import {
  IDENTITY_INFO_FORM_HELP_TEXT,
  IDENTITY_INFO_FORM_LABELS
} from '@/lib/constants/identityInfo';
import PersonalInfoForm from '@/components/personalInfoForm/personalInforForm';
import {
  usePhoneDobTranslations,
  useRxLabelTranslations
} from '../../lib/utils/translationHelpers';
import Heart from '../../public/images/heart.svg';
import { phoneDOBSchema } from '@/lib/dataSchemas/phoneDOBSchema';
import { CVSData } from '@/lib/cvsdata/cvsdata';
import useAlertProvider from '@/components/alert/useAlertProvider';
import { phoneDOBErrorDesc } from '@/components/errorMessages/errorMessages';
import { SIGN_IN_ROUTE } from '@/lib/constants/routes';
import RxLabelForm from '@/components/rxLabelForm/rxLabelForm';
import { rxLabelSchema } from '@/lib/dataSchemas/rxLabelSchema';
import UserContext from '@/context/UserContext';
import { CONNECT_ANOTHER_WAY } from '@/lib/constants/connectAnotherWay';
import { DOB_ERROR_MSG, DOB_LABELS } from '@/lib/constants/dateOfBirth';

const phoneDOBFormSchema = phoneDOBSchema();
const rxLabelFormSchema = rxLabelSchema();
const reValidateMode: 'onSubmit' | undefined = 'onSubmit';

const PersonalInfoLookup: FC = (): ReactElement | null => {
  const lob = findLob();
  const isUnifiedRegistration = [CAREMARK_LOB, SPECIALTY_LOB].includes(lob);
  const isHydrated = useIsHydrated();
  const PAGE_CONS = usePhoneDobTranslations();
  const RX_LABEL_CONS = useRxLabelTranslations();
  const checkUsersAccount = useCheckUsersAccount();
  const { setAlerts } = useAlertProvider();
  const [dynSchema, setDynSchema] = useState(undefined);
  const formState = useForm<FormData>({
    context: {
      dynSchema
    },
    resolver: async (data, context, options) => {
      return yupResolver(dynSchema)(data, context, options);
    },
    reValidateMode
  });
  const { isReady, query } = useRouter();
  const formName = 'phone-dob-form';
  const [formData, setFormData] = useState({});
  const [campaignInfo, setCampaignInfo] = useState({
    lob: '',
    authType: '',
    campaignId: '',
    campaignFUrl: '',
    campaignGUrl: '',
    programName: '',
    ezAccountCreation: false
  });
  const {
    isPending: lookupMutationLoading,
    mutate: guestPhoneDOBLookupMutation
  } = useGuestPhoneDOBLookup(formData, campaignInfo);
  const { GUEST_SOURCE, RX_LABEL_SOURCE } = getLobData();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [isPending, setisPending] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const { lookupData, updateLookupData } = UserContext();
  const spinner = lookupMutationLoading || checkUsersAccount.isPending;
  // campaign ids for which we will hide the sign in button
  const hideSignInCampaignIds = ['10010'];

  useEffect(() => {
    if (!isReady) return;
    setPageLoaded(true);
    updateLookupData({
      phoneDOBInfoFormFlag: query?.rxLabel !== 'true'
    });
    CVSData.logout(false);
    if (query && query?.prg) {
      getCampaignInfo(2);
      return;
    }
  }, [isReady]);

  useEffect(() => {
    setisPending(lookupMutationLoading || checkUsersAccount.isPending);
  }, [spinner]);

  useEffect(() => {
    // clear alerts on form swap and update schema
    setAlerts([]);
    setDynSchema(lookupData?.phoneDOBInfoFormFlag ? phoneDOBFormSchema : rxLabelFormSchema);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    pageLoaded &&
      pageLoadAnalytics(tags.selfServe, lookupData?.phoneDOBInfoFormFlag ? tags.phoneDOB : tags.rx);
  }, [lookupData?.phoneDOBInfoFormFlag]);

  const getCampaignInfo = async (tries: number) => {
    const campaignRes = await CVSData.getCampaignInfo(query?.prg.toString());
    const campaignInfo = campaignRes?.data?.response;
    if (!campaignInfo && tries > 0) {
      //retry call on failure
      getCampaignInfo(tries - 1);
    } else {
      setCampaignInfo({ ...campaignInfo });
      setShowSignIn(!hideSignInCampaignIds.includes(campaignInfo?.campaignId));
    }
  };
  const submitData = (fieldValues) => {
    clickAnalytics({
      payload: {
        link_name: `${tags.account}:${tags.selfServe}:${
          lookupData?.phoneDOBInfoFormFlag ? tags.phoneDOB : tags.rx
        }:${tags.search}`
      }
    });
    if (!campaignInfo?.campaignGUrl) {
      setTimeout(() => {
        setAlerts([
          {
            title: PAGE_CONS?.LOOKUP_ERRORS?.tech_error?.title,
            message: phoneDOBErrorDesc(PAGE_CONS?.LOOKUP_ERRORS?.tech_error?.description)
          }
        ]);
      }, 10);
      return;
    } else {
      const { dateOfBirth, phoneNumber, prescriptionNumber, storeNumber } = fieldValues;
      const { mm, dd, yyyy } = parseDobString(dateOfBirth);
      const dob = `${yyyy}-${mm}-${dd}`;
      if (lookupData?.phoneDOBInfoFormFlag) {
        const ph = convertPhoneFormatForAPI(phoneNumber);
        setFormData({
          phoneNumber: ph,
          dateOfBirth: dob,
          source: GUEST_SOURCE
        });
      } else {
        setFormData({
          prescriptionNumber,
          storeNumber,
          source: RX_LABEL_SOURCE,
          dateOfBirth: dob
        });
      }
      guestPhoneDOBLookupMutation();
    }
  };

  if (!isHydrated) {
    return null;
  }

  const redirectToSignIn = () => {
    window.location.href =
      SIGN_IN_ROUTE +
      `${
        !isSpecialty() && !isCaremark()
          ? '?returnUrl=' + encodeURI('/account/onboarding/landing?source=PHARMACY')
          : ''
      }`;
  };

  const switchForms = () => {
    updateLookupData({ phoneDOBInfoFormFlag: !lookupData?.phoneDOBInfoFormFlag });
    clickAnalytics({
      payload: {
        link_name: `${tags.account}:${tags.selfServe}:${
          lookupData?.phoneDOBInfoFormFlag ? tags.phoneDOB : tags.rx
        }:${lookupData?.phoneDOBInfoFormFlag ? tags.useRxLabel : tags.usePersonalInfo}`
      }
    });
  };
  return (
    <AppLayout
      pageTitle={LOOKUP_PAGE_TITLE}
      pageType={PAGE_TYPE}
      showConnectCard={isPending ? false : true}
      pageName="Phone-DOB Page"
      action={'/account-auth/mfa'}
      crossLOBUI
      showFooter
    >
      <>
        {isPending && <Spinner />}
        <Heart className={formStyles.heart} role="img" />
        <div className={styles.phoneDOB__container}>
          <Form
            formState={formState.formState}
            errorTitle={PAGE_CONS?.PERSONAL_INFO_ERROR_TITLES?.DEFAULT}
            onSubmit={formState.handleSubmit(submitData)}
            submitBtnText={PAGE_CONS?.CONTINUE_BUTTON_TEXT}
            data-test-id={formName}
            cancelTag={`${tags.account}:${tags.selfServe}:${
              lookupData?.phoneDOBInfoFormFlag ? tags.phoneDOB : tags.rx
            }:${tags.cancel}`}
            isUnifiedRegistration
            dobErrorMessages={{ ...PERSONAL_INFO_VALIDATION, ...DOB_ERROR_MSG }}
          >
            <h1 className={`${styles.phoneDOB__header} pc-heading-xxl`}>
              {PAGE_CONS?.CONS?.title}
            </h1>
            <p className={`${styles.phoneDOB__description} pc-paragraph-m`}>
              {PAGE_CONS?.CONS?.description}
            </p>
            <PageAlerts />
            <div className={styles.phoneDOB__form}>
              {lookupData?.phoneDOBInfoFormFlag ? (
                <PersonalInfoForm
                  formStatus={formState}
                  dobHelper={PAGE_CONS?.CONS?.dob_helper}
                  phoneDOB
                  isUnifiedRegistration
                  dobShowHide
                  dobShowHideChecked={!!query?.prg}
                  translations={{
                    IDENTITY_INFO_FORM_LABELS:
                      PAGE_CONS?.PHONE_DOB_FORM_LABELS || IDENTITY_INFO_FORM_LABELS,
                    IDENTITY_INFO_FORM_HELP_TEXT,
                    DOB_LABELS: {
                      DATE_OF_BIRTH: PAGE_CONS?.CONS?.dob || DOB_LABELS.DATE_OF_BIRTH,
                      DATE_OF_BIRTH_HELPER:
                        PAGE_CONS?.CONS?.dob_helper || DOB_LABELS.DATE_OF_BIRTH_HELPER,
                      ...DOB_LABELS
                    }
                  }}
                  analytics={{ flowName: tags.selfServe, pageName: tags.phoneDOB }}
                />
              ) : (
                <RxLabelForm
                  formStatus={formState}
                  readRxLabelInfo={RX_LABEL_CONS?.READ_RX_LABEL_INFO}
                  allFieldsRequired={RX_LABEL_CONS?.ALL_FIELDS_REQUIRED}
                  storeLocator={RX_LABEL_CONS?.STORE_LOCATOR}
                  rxLabelFormLabels={RX_LABEL_CONS?.RXLABEL_FORM_LABELS}
                  rxLabelFormModalText={RX_LABEL_CONS?.RXLABEL_FORM_MODAL_TEXT}
                  analytics={{ flowName: tags.selfServe, pageName: tags.rx }}
                />
              )}
            </div>
          </Form>
          {!isUnifiedRegistration && (
            <>
              <hr />
              <h2 className={`${styles.phoneDOB__footer} pc-heading-m`}>{CONNECT_ANOTHER_WAY}</h2>
              <div>
                <Link
                  href={'javascript:;'}
                  variant="standalone"
                  onClick={() => {
                    switchForms();
                  }}
                >
                  {lookupData?.phoneDOBInfoFormFlag
                    ? PAGE_CONS?.CONS.use_rx_label
                    : PAGE_CONS?.CONS.use_phone_dob}
                </Link>
              </div>
            </>
          )}
          {showSignIn && (
            <>
              <hr />
              <h2 className={`${styles.phoneDOB__footer} pc-heading-m`}>
                {PAGE_CONS?.CONS?.have_an_account}
              </h2>
              <Link
                href={'javascript:;'}
                variant="standalone"
                weight="strong"
                onClick={() => redirectToSignIn()}
              >
                {PAGE_CONS?.CONS?.sign_in}
              </Link>
            </>
          )}
        </div>
      </>
    </AppLayout>
  );
};

export default PersonalInfoLookup;
